import React, { Fragment } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Icon,
  IconButton,
  Tooltip,
  alpha,
  Drawer,
  Divider,
  Chip,
  Badge,
  Menu,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  purchaseRequestService,
  ActivityLog,
  ActivityLogsResponse,
} from "../../services";
import { PurchaseRequestFormDialog } from "./index";
import { DeleteConfirmationDialog } from "../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import {
  ActionConfirmationDialog,
  ActionType,
} from "../../components/ActionConfirmationDialog/ActionConfirmationDialog";
import { RejectionReasonDialog } from "../../components/RejectionReasonDialog/RejectionReasonDialog";
import { ActivityTimeline } from "../../components/ActivityTimeline/ActivityTimeline";
import { EmptyState } from "../../../../components/EmptyState/EmptyState";
import {
  IESDoc,
  TableColumn,
  WithId,
  FilterGroup,
  QuickFilter,
} from "../../../../app.interface";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { AppTable } from "../../../../components/AppTable";
import { AppSearch } from "../../../../components/AppSearch";
import { PageHelper } from "../../components/PageHelper/PageHelper";
import { useAuth } from "../../../auth/hooks/useAuth";
import { hasPermission } from "../../../auth/utils/permission.utils";

interface PurchaseRequestItem {
  item_id: string;
  item_name: string;
  uom: string;
  quantity: number;
  reason: string;
  urgency: "Low" | "Medium" | "High";
}

interface PurchaseRequestFormData {
  items: PurchaseRequestItem[];
  status?: string;
  created_at?: Date;
  created_by?: string;
  submitted_by?: string;
  submitted_on?: Date;
  rejection_reason?: string;
  requestId?: string;
}

// Enhanced status configuration
interface StatusConfig {
  label: string;
  color: string;
  icon: string;
  bgColor: string;
  canEdit: boolean;
  canDelete: boolean;
  canSubmit: boolean;
  canReopen?: boolean;
}

const statusConfig: Record<string, StatusConfig> = {
  draft: {
    label: "Draft",
    color: "#64748B",
    icon: "edit",
    bgColor: "#F1F5F9",
    canEdit: true,
    canDelete: true,
    canSubmit: true,
  },
  submitted: {
    label: "Submitted",
    color: "#3B82F6",
    icon: "send",
    bgColor: "#EFF6FF",
    canEdit: false,
    canDelete: false,
    canSubmit: false,
  },
  approved: {
    label: "Approved",
    color: "#059669",
    icon: "check_circle",
    bgColor: "#ECFDF5",
    canEdit: false,
    canDelete: false,
    canSubmit: false,
  },
  rejected: {
    label: "Rejected",
    color: "#DC2626",
    icon: "cancel",
    bgColor: "#FEF2F2",
    canEdit: false,
    canDelete: false,
    canSubmit: false,
    canReopen: true,
  },
  reopened: {
    label: "Reopened",
    color: "#F59E0B",
    icon: "refresh",
    bgColor: "#FFFBEB",
    canEdit: true,
    canDelete: true,
    canSubmit: true,
  },
};

// Utility to sanitize payload before sending to backend
const sanitizePurchaseRequestPayload = (payload: any) => {
  // Remove all fields not allowed by backend, but keep _id
  const {
    _index,
    _score,
    id,
    requestId,
    status,
    created_by,
    created_by_email,
    created_at,
    updated_at,
    business_id,
    is_deleted,
    submitted_by,
    submitted_by_email,
    submitted_on,
    rejection_reason,
    total_cost,
    price,
    cost_per_unit,
    rejected_by,
    rejected_by_email,
    rejected_on,
    approved_by,
    approved_by_email,
    approved_on,
    activity_logs,
    reopened_by,
    reopened_by_email,
    reopened_on,
    ...rest
  } = payload;
  return rest;
};

export const PurchaseRequests: React.FC = () => {
  const [purchaseRequests, setPurchaseRequests] = React.useState<IESDoc[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [formDialogOpen, setFormDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedRequest, setSelectedRequest] = React.useState<IESDoc | null>(
    null
  );
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [totalItems, setTotalItems] = React.useState(0);
  const [sortField, setSortField] = React.useState<string>("created_at");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");
  const [search, setSearch] = React.useState("");
  const [filters, setFilters] = React.useState<Record<string, any>>({});
  const [selectedRequestDetails, setSelectedRequestDetails] =
    React.useState<IESDoc | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuRequestId, setMenuRequestId] = React.useState<string | null>(null);

  // New confirmation dialog states
  const [actionConfirmationOpen, setActionConfirmationOpen] =
    React.useState(false);
  const [actionType, setActionType] = React.useState<ActionType>("submit");
  const [pendingAction, setPendingAction] = React.useState<(() => void) | null>(
    null
  );
  const [rejectionDialogOpen, setRejectionDialogOpen] = React.useState(false);
  const [pendingRejectionRequestId, setPendingRejectionRequestId] =
    React.useState<string | null>(null);

  // Activity logs states
  const [activityLogs, setActivityLogs] = React.useState<ActivityLog[]>([]);
  const [activityLoading, setActivityLoading] = React.useState(false);
  const [activityTotal, setActivityTotal] = React.useState(0);
  const [activityPage, setActivityPage] = React.useState(1);
  const [activityPageSize] = React.useState(10);

  // Drawer tab state
  const [drawerTab, setDrawerTab] = React.useState(0);

  // Get current user from auth context
  const { user } = useAuth();
  const currentUserId = user?.id; // Get from auth context

  // Helper functions for confirmation dialogs
  const showActionConfirmation = (
    type: ActionType,
    action: () => void,
    request?: IESDoc
  ) => {
    setActionType(type);
    setPendingAction(() => action);
    setSelectedRequest(request || null);
    setActionConfirmationOpen(true);
  };

  const handleActionConfirm = async () => {
    if (pendingAction) {
      await pendingAction();
    }
    setActionConfirmationOpen(false);
    setPendingAction(null);
    setSelectedRequest(null);
  };

  const handleActionCancel = () => {
    setActionConfirmationOpen(false);
    setPendingAction(null);
    setSelectedRequest(null);
  };

  const showRejectionDialog = (requestId: string) => {
    setPendingRejectionRequestId(requestId);
    setRejectionDialogOpen(true);
  };

  const handleRejectionConfirm = async (reason: string) => {
    if (pendingRejectionRequestId) {
      await handleRejectRequest(pendingRejectionRequestId, reason);
    }
    setRejectionDialogOpen(false);
    setPendingRejectionRequestId(null);
  };

  const handleRejectionCancel = () => {
    setRejectionDialogOpen(false);
    setPendingRejectionRequestId(null);
  };

  // Load activity logs for selected request
  const loadActivityLogs = async (requestId: string, page: number = 1) => {
    setActivityLoading(true);
    try {
      const response =
        await purchaseRequestService.getPurchaseRequestActivityLogs(requestId, {
          pageIndex: page - 1, // API uses 0-based indexing
          pageSize: activityPageSize,
        });
      setActivityLogs(response.data);
      setActivityTotal(response.total);
      setActivityPage(page);
    } catch (error: any) {
      console.error("Error loading activity logs:", error);
      toast.error("Failed to load activity history");
    } finally {
      setActivityLoading(false);
    }
  };

  const handleActivityPageChange = (page: number) => {
    if (selectedRequestDetails) {
      loadActivityLogs(selectedRequestDetails._id, page);
    }
  };

  const filterGroups: FilterGroup[] = [
    {
      id: "status",
      label: "Status",
      type: "select",
      icon: "fiber_manual_record",
      description: "Filter requests by their current status",
      options: [
        {
          id: "draft",
          label: "Draft",
          value: "draft",
          icon: "edit",
          color: "#64748B",
          description: "Draft requests (editable)",
        },
        {
          id: "submitted",
          label: "Submitted",
          value: "submitted",
          icon: "send",
          color: "#3B82F6",
          description: "Submitted for approval",
        },
        {
          id: "approved",
          label: "Approved",
          value: "approved",
          icon: "check_circle",
          color: "#059669",
          description: "Approved requests",
        },
        {
          id: "rejected",
          label: "Rejected",
          value: "rejected",
          icon: "cancel",
          color: "#DC2626",
          description: "Rejected requests",
        },
        {
          id: "reopened",
          label: "Reopened",
          value: "reopened",
          icon: "refresh",
          color: "#F59E0B",
          description: "Reopened for editing",
        },
      ],
    },
    {
      id: "urgency",
      label: "Urgency",
      type: "select",
      icon: "priority_high",
      description: "Filter by urgency level",
      options: [
        {
          id: "high",
          label: "High",
          value: "high",
          icon: "priority_high",
          color: "#DC2626",
        },
        {
          id: "medium",
          label: "Medium",
          value: "medium",
          icon: "remove",
          color: "#F59E0B",
        },
        {
          id: "low",
          label: "Low",
          value: "low",
          icon: "low_priority",
          color: "#059669",
        },
      ],
    },
    {
      id: "sortBy",
      label: "Sort By",
      type: "select",
      icon: "sort",
      description: "Sort requests by field",
      options: [
        {
          id: "created_desc",
          label: "Newest First",
          value: "created_desc",
          icon: "update",
        },
        {
          id: "created_asc",
          label: "Oldest First",
          value: "created_asc",
          icon: "schedule",
        },
        {
          id: "submitted_desc",
          label: "Recently Submitted",
          value: "submitted_desc",
          icon: "send",
        },
        {
          id: "urgency_desc",
          label: "High Urgency First",
          value: "urgency_desc",
          icon: "priority_high",
        },
      ],
    },
  ];

  const getStatusChip = (status: string) => {
    const config = statusConfig[status as keyof typeof statusConfig];
    if (!config) return null;

    return (
      <Chip
        label={config.label}
        size="small"
        icon={<Icon sx={{ fontSize: "16px !important" }}>{config.icon}</Icon>}
        sx={{
          backgroundColor: config.bgColor,
          color: config.color,
          fontWeight: 600,
          border: `1px solid ${alpha(config.color, 0.2)}`,
          "& .MuiChip-icon": {
            color: config.color,
          },
        }}
      />
    );
  };

  const getActionButtons = (
    row: WithId & Record<string, any>
  ): React.ReactElement => {
    const status = row.status || "draft";
    const config = statusConfig[status];

    return (
      <div className="flex gap-2 justify-end">
        {/* Edit button - only for editable statuses */}
        {config && config.canEdit && (
          <Tooltip title="Edit Request" arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const originalRequest = purchaseRequests.find(
                  (r) => r._id === row._id
                );
                handleOpenFormDialog(originalRequest);
              }}
              disabled={
                !hasPermission(
                  "inventory.purchase_requests.edit",
                  user?.newPermissions,
                  user?.assignment_details?.id,
                  user?.is_store_owner
                )
              }
              sx={{
                width: 34,
                height: 34,
                bgcolor: (theme) => alpha("#0891B2", 0.08),
                color: "#0891B2",
                "&:hover": {
                  bgcolor: (theme) => alpha("#0891B2", 0.16),
                },
              }}
            >
              <Icon fontSize="small">edit</Icon>
            </IconButton>
          </Tooltip>
        )}

        {/* Submit button - only for draft/reopened status */}
        {config && config.canSubmit && (
          <Tooltip title="Submit Request" arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                showActionConfirmation(
                  "submit",
                  () => handleSubmitRequest(row),
                  row
                );
              }}
              disabled={
                !hasPermission(
                  "inventory.purchase_requests.submit",
                  user?.newPermissions,
                  user?.assignment_details?.id,
                  user?.is_store_owner
                )
              }
              sx={{
                width: 34,
                height: 34,
                bgcolor: (theme) => alpha("#059669", 0.08),
                color: "#059669",
                "&:hover": {
                  bgcolor: (theme) => alpha("#059669", 0.16),
                },
              }}
            >
              <Icon fontSize="small">send</Icon>
            </IconButton>
          </Tooltip>
        )}

        {/* Actions menu for submitted requests */}
        {status === "submitted" && (
          <Tooltip title="Actions" arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setAnchorEl(e.currentTarget);
                setMenuRequestId(row._id);
              }}
              sx={{
                width: 34,
                height: 34,
                bgcolor: (theme) => alpha("#3B82F6", 0.08),
                color: "#3B82F6",
                "&:hover": {
                  bgcolor: (theme) => alpha("#3B82F6", 0.16),
                },
              }}
            >
              <Icon fontSize="small">more_vert</Icon>
            </IconButton>
          </Tooltip>
        )}

        {/* Reopen button - only for rejected status */}
        {config && config.canReopen && (
          <Tooltip title="Reopen Request" arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                showActionConfirmation(
                  "reopen",
                  () => handleReopenRequest(row),
                  row
                );
              }}
              disabled={
                !hasPermission(
                  "inventory.purchase_requests.reopen",
                  user?.newPermissions,
                  user?.assignment_details?.id,
                  user?.is_store_owner
                )
              }
              sx={{
                width: 34,
                height: 34,
                bgcolor: (theme) => alpha("#F59E0B", 0.08),
                color: "#F59E0B",
                "&:hover": {
                  bgcolor: (theme) => alpha("#F59E0B", 0.16),
                },
              }}
            >
              <Icon fontSize="small">refresh</Icon>
            </IconButton>
          </Tooltip>
        )}

        {/* Delete button - only for deletable statuses */}
        {config && config.canDelete && (
          <Tooltip title="Delete Request" arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteDialog(row);
              }}
              disabled={
                !hasPermission(
                  "inventory.purchase_requests.delete",
                  user?.newPermissions,
                  user?.assignment_details?.id,
                  user?.is_store_owner
                )
              }
              sx={{
                width: 34,
                height: 34,
                bgcolor: (theme) => alpha("#DC2626", 0.08),
                color: "#DC2626",
                "&:hover": {
                  bgcolor: (theme) => alpha("#DC2626", 0.16),
                },
              }}
            >
              <Icon fontSize="small">delete</Icon>
            </IconButton>
          </Tooltip>
        )}
      </div>
    );
  };

  const MasterColumns: TableColumn[] = [
    {
      id: "requestId",
      header: "Request ID",
      field: "requestId",
      type: "action",
      width: "200px",
      renderAction: (row) => {
        const status = row.status || "draft";
        const config = statusConfig[status as keyof typeof statusConfig];
        const hasRequestId = !!row.requestId;
        return (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <Box>
              {status === "draft" || !hasRequestId ? (
                <Tooltip
                  title="Submit this purchase request to generate a Request ID."
                  arrow
                >
                  <Chip
                    label="Not Generated Yet"
                    size="small"
                    sx={{
                      bgcolor: "grey.100",
                      color: "grey.600",
                      fontWeight: 500,
                      fontSize: "0.95em",
                      height: 24,
                      px: 1.5,
                      letterSpacing: 0.2,
                      borderRadius: 1,
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              ) : (
                <Typography fontWeight={600} sx={{ fontSize: "0.875rem" }}>
                  {row.requestId?.toUpperCase()}
                </Typography>
              )}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 0.25,
                }}
              >
                <Icon
                  sx={{
                    fontSize: "12px",
                    color: config?.color || "#64748B",
                  }}
                >
                  {config?.icon || "description"}
                </Icon>
                <Typography
                  variant="caption"
                  sx={{
                    color: config?.color || "#64748B",
                    fontWeight: 500,
                  }}
                >
                  {config?.label || status}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      },
    },
    {
      id: "items_count",
      header: "Items",
      field: "items_count",
      type: "action",
      width: "100px",
      renderAction: (row) => (
        <Chip
          label={`${row.items?.length || 0} item${
            (row.items?.length || 0) !== 1 ? "s" : ""
          }`}
          size="small"
          variant="outlined"
          color="primary"
        />
      ),
    },
    {
      id: "urgency",
      header: "Urgency",
      field: "urgency",
      type: "status",
      width: "120px",
      sortable: true,
    },
    {
      id: "status",
      header: "Status",
      field: "status",
      type: "action",
      width: "140px",
      sortable: true,
      renderAction: (row) =>
        getStatusChip(row.status || "draft") ?? (
          <Typography>Unknown</Typography>
        ),
    },
    {
      id: "created_by",
      header: "Created By",
      field: "created_by",
      type: "action",
      width: "180px",
      renderAction: (row) => (
        <Typography variant="body2">
          {row.created_by_email || row.created_by || "Unknown"}
        </Typography>
      ),
    },
    {
      id: "submitted_info",
      header: "Submitted",
      field: "submitted_info",
      type: "action",
      width: "180px",
      renderAction: (row) => {
        if (!row.submitted_on) {
          return (
            <Typography variant="body2" color="textSecondary">
              -
            </Typography>
          );
        }

        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {new Date(row.submitted_on).toLocaleDateString()}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              by{" "}
              {row.submitted_by_email ||
                row.submitted_by ||
                row.created_by_email ||
                row.created_by}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: "created_at",
      header: "Created",
      field: "created_at",
      type: "datetime",
      width: "150px",
      sortable: true,
    },
    {
      id: "actions",
      header: "",
      field: "actions",
      type: "action",
      width: "140px",
      renderAction: getActionButtons,
    },
  ];

  React.useEffect(() => {
    loadPurchaseRequests();
  }, [page, pageSize, sortField, sortOrder, search, filters]);

  const loadPurchaseRequests = async () => {
    setLoading(true);
    try {
      // Add filterTitles mapping for filter titles
      const filterTitles: Record<string, string> = {
        status: "Status",
        urgency: "Urgency",
        sortBy: "Sort By",
        created_by: "Created By",
        // Add more mappings as needed
      };

      const params = {
        searchQuery: search,
        sortField,
        sortOrder,
        pageIndex: page,
        pageSize,
        filters: Object.keys(filters).map((key) => ({
          id: key,
          field: key,
          title: filterTitles[key] || key,
          value: filters[key],
          operator: "eq",
        })),
      };
      const response = await purchaseRequestService.listPurchaseRequests(
        params
      );
      setPurchaseRequests(response.data || []);
      setTotalItems(response.total || 0);
    } catch (error) {
      console.error("Error loading purchase requests:", error);
      toast.error("Failed to load purchase requests");
    } finally {
      setLoading(false);
    }
  };

  const handleSavePurchaseRequest = async (data: PurchaseRequestFormData) => {
    setSaving(true);
    try {
      if (selectedRequest) {
        await purchaseRequestService.updatePurchaseRequest(
          sanitizePurchaseRequestPayload({
            ...data,
            _id: selectedRequest._id,
            status: data.status || selectedRequest.status || "draft",
          }) as any
        );
        toast.success("Purchase request updated successfully");
      } else {
        await purchaseRequestService.createPurchaseRequest({
          ...data,
          status: "draft",
          created_by: currentUserId,
          business_id: user?.store_id || user?.business_id,
        } as any);
        toast.success("Purchase request created successfully");
      }
      handleCloseFormDialog();
      loadPurchaseRequests();
    } catch (error: any) {
      console.error("Error saving purchase request:", error);
      toast.error(error.message || "Failed to save purchase request");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitRequest = async (request: IESDoc) => {
    setSubmitting(true);
    try {
      // Generate requestId if not exists
      const requestId = request.requestId || `PR${Date.now()}`;

      const response = await purchaseRequestService.submitPurchaseRequest(
        sanitizePurchaseRequestPayload({
          ...request,
          status: "submitted",
          submitted_by: currentUserId,
          submitted_on: Date.now(),
          requestId: requestId,
          business_id: user?.store_id || user?.business_id,
        }) as any
      );

      // Show both requestId and _id from the response
      const responseRequestId = response?.data?.requestId || requestId;
      const responseId = response?.data?._id || response?.data?.id;

      toast.success(
        `Purchase request submitted successfully. Request ID: ${responseRequestId}${
          responseId ? ` | ID: ${responseId}` : ""
        }`
      );
      loadPurchaseRequests();
    } catch (error: any) {
      console.error("Error submitting purchase request:", error);
      toast.error(error.message || "Failed to submit purchase request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveRequest = async (requestId: string) => {
    try {
      await purchaseRequestService.approvePurchaseRequest(requestId);
      toast.success("Purchase request approved successfully");
      loadPurchaseRequests();
      setAnchorEl(null);
      setMenuRequestId(null);
    } catch (error: any) {
      console.error("Error approving purchase request:", error);
      toast.error(error.message || "Failed to approve purchase request");
    }
  };

  const handleRejectRequest = async (requestId: string, reason: string) => {
    try {
      await purchaseRequestService.rejectPurchaseRequest(requestId, reason);
      toast.success("Purchase request rejected");
      loadPurchaseRequests();
      setAnchorEl(null);
      setMenuRequestId(null);
    } catch (error: any) {
      console.error("Error rejecting purchase request:", error);
      toast.error(error.message || "Failed to reject purchase request");
    }
  };

  const handleReopenRequest = async (request: IESDoc) => {
    try {
      // Call the /reopen endpoint instead of update
      await purchaseRequestService.reopenPurchaseRequest(request._id);
      toast.success("Purchase request reopened for editing");
      loadPurchaseRequests();
    } catch (error: any) {
      console.error("Error reopening purchase request:", error);
      toast.error(error.message || "Failed to reopen purchase request");
    }
  };

  const handleDeletePurchaseRequest = async () => {
    if (!selectedRequest) return;
    try {
      await purchaseRequestService.deletePurchaseRequest(selectedRequest._id);
      toast.success("Purchase request deleted successfully");
      handleCloseDeleteDialog();
      loadPurchaseRequests();
    } catch (error: any) {
      console.error("Error deleting purchase request:", error);
      toast.error(error.message || "Failed to delete purchase request");
    }
  };

  const handleOpenFormDialog = (request?: IESDoc) => {
    setSelectedRequest(request || null);
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setSelectedRequest(null);
  };

  const handleOpenDeleteDialog = (request: IESDoc) => {
    setSelectedRequest(request);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedRequest(null);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleRowClick = (row: WithId & Record<string, any>) => {
    const request = purchaseRequests.find((r) => r._id === row._id);
    setSelectedRequestDetails(request || null);
    setDrawerTab(0); // Reset to first tab
    if (request) {
      loadActivityLogs(request._id, 1); // Load first page of activity logs
    }
  };

  const handleDrawerClose = () => {
    setSelectedRequestDetails(null);
    setActivityLogs([]);
    setActivityTotal(0);
    setActivityPage(1);
    setDrawerTab(0);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRequestId(null);
  };

  // Get summary counts for different statuses
  const getStatusCounts = () => {
    const counts = {
      draft: 0,
      submitted: 0,
      approved: 0,
      rejected: 0,
      reopened: 0,
    };

    purchaseRequests.forEach((pr) => {
      const status = pr.status || "draft";
      if (counts.hasOwnProperty(status)) {
        counts[status as keyof typeof counts]++;
      }
    });

    return counts;
  };

  const statusCounts = getStatusCounts();

  if (loading && purchaseRequests.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading purchase requests...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <PageHeader
        title="Purchase Requests"
        subtitle="Create and manage purchase requests"
        icon="shopping_cart"
        actions={
          <Button
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={() => handleOpenFormDialog()}
            disabled={
              !hasPermission(
                "inventory.purchase_requests.create",
                user?.newPermissions,
                user?.assignment_details?.id,
                user?.is_store_owner
              )
            }
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            Create New PR
          </Button>
        }
      />

      <Box sx={{ flex: 1, p: 3, pt: 0 }}>
        {/* Status Summary Cards */}
        <Box sx={{ mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
          {Object.entries(statusCounts).map(([status, count]) => {
            const config = statusConfig[status as keyof typeof statusConfig];
            if (!config) return null;

            return (
              <Paper
                key={status}
                sx={{
                  p: 2,
                  minWidth: 140,
                  bgcolor: config.bgColor,
                  border: `1px solid ${alpha(config.color, 0.2)}`,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 2,
                  },
                }}
                onClick={() => setFilters({ ...filters, status })}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <Icon sx={{ color: config.color, fontSize: 20 }}>
                    {config.icon}
                  </Icon>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: config.color, fontWeight: 600 }}
                  >
                    {config.label}
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{ color: config.color, fontWeight: 700 }}
                >
                  {count}
                </Typography>
              </Paper>
            );
          })}
        </Box>

        <Box sx={{ mb: 3 }}>
          <AppSearch
            onSearch={setSearch}
            onFilterChange={setFilters}
            placeholder="Search purchase requests..."
            filterGroups={filterGroups}
            defaultFilters={filters}
          />
        </Box>

        {purchaseRequests.length === 0 && !loading ? (
          <EmptyState
            icon="shopping_cart"
            title="No Purchase Requests Found"
            description="Get started by creating your first purchase request"
            actionText="Create New PR"
            onAction={() => handleOpenFormDialog()}
          />
        ) : (
          <AppTable
            data={purchaseRequests}
            columns={MasterColumns}
            loading={loading}
            pagination={{
              total: totalItems,
              page: page,
              pageSize: pageSize,
              onPageChange: setPage,
              onPageSizeChange: setPageSize,
            }}
            onSort={handleSort}
            sortConfig={{
              field: sortField,
              direction: sortOrder,
            }}
            onRowClick={handleRowClick}
          />
        )}
      </Box>

      {/* Actions Menu for Business Admin */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { minWidth: 180 },
        }}
      >
        <MenuItem
          onClick={() => {
            if (menuRequestId) {
              const request = purchaseRequests.find(
                (r) => r._id === menuRequestId
              );
              showActionConfirmation(
                "approve",
                () => handleApproveRequest(menuRequestId),
                request
              );
              handleMenuClose();
            }
          }}
          disabled={
            !hasPermission(
              "inventory.purchase_requests.approve",
              user?.newPermissions,
              user?.assignment_details?.id,
              user?.is_store_owner
            )
          }
          sx={{ color: "#059669" }}
        >
          <Icon sx={{ mr: 2, fontSize: 20 }}>check_circle</Icon>
          Approve
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuRequestId) {
              showRejectionDialog(menuRequestId);
              handleMenuClose();
            }
          }}
          disabled={
            !hasPermission(
              "inventory.purchase_requests.reject",
              user?.newPermissions,
              user?.assignment_details?.id,
              user?.is_store_owner
            )
          }
          sx={{ color: "#DC2626" }}
        >
          <Icon sx={{ mr: 2, fontSize: 20 }}>cancel</Icon>
          Reject
        </MenuItem>
        {/* Only show Create RFQ for approved requests */}
        {(() => {
          if (menuRequestId) {
            const request = purchaseRequests.find(
              (r) => r._id === menuRequestId
            );
            if (request?.status === "approved") {
              return (
                <MenuItem
                  onClick={() => {
                    // Navigate to RFQ creation with this PR
                    window.location.href = `/business/procurement/rfq?pr=${request._id}`;
                    handleMenuClose();
                  }}
                  disabled={
                    !hasPermission(
                      "inventory.rfq.create",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                  sx={{ color: "#3B82F6" }}
                >
                  <Icon sx={{ mr: 2, fontSize: 20 }}>request_quote</Icon>
                  Create RFQ
                </MenuItem>
              );
            }
          }
          return null;
        })()}
      </Menu>

      <PurchaseRequestFormDialog
        open={formDialogOpen}
        onClose={handleCloseFormDialog}
        onSave={handleSavePurchaseRequest}
        purchaseRequest={selectedRequest}
        loading={saving}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeletePurchaseRequest}
        title="Delete Purchase Request"
        itemName={
          selectedRequest
            ? selectedRequest.requestId?.toUpperCase() ||
              selectedRequest._id?.toUpperCase()
            : ""
        }
        itemDescription="This action cannot be undone and all associated data will be permanently deleted."
        loading={false}
      />

      {/* Action Confirmation Dialog */}
      <ActionConfirmationDialog
        open={actionConfirmationOpen}
        onClose={handleActionCancel}
        onConfirm={handleActionConfirm}
        actionType={actionType}
        loading={submitting}
        requestId={
          selectedRequest
            ? selectedRequest.requestId?.toUpperCase() ||
              selectedRequest._id?.toUpperCase()
            : undefined
        }
        itemName={
          selectedRequest
            ? `${selectedRequest.items?.length || 0} item${
                (selectedRequest.items?.length || 0) !== 1 ? "s" : ""
              }`
            : undefined
        }
      />

      {/* Rejection Reason Dialog */}
      <RejectionReasonDialog
        open={rejectionDialogOpen}
        onClose={handleRejectionCancel}
        onConfirm={handleRejectionConfirm}
        loading={false}
        requestId={
          pendingRejectionRequestId
            ? (() => {
                const request = purchaseRequests.find(
                  (r) => r._id === pendingRejectionRequestId
                );
                return (
                  request?.requestId?.toUpperCase() ||
                  request?._id?.toUpperCase()
                );
              })()
            : undefined
        }
        itemName={
          pendingRejectionRequestId
            ? (() => {
                const request = purchaseRequests.find(
                  (r) => r._id === pendingRejectionRequestId
                );
                return `${request?.items?.length || 0} item${
                  (request?.items?.length || 0) !== 1 ? "s" : ""
                }`;
              })()
            : undefined
        }
      />

      <Drawer
        anchor="right"
        open={!!selectedRequestDetails}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: 600,
            bgcolor: "background.default",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {selectedRequestDetails && (
          <>
            {/* Header */}
            <Box sx={{ p: 3, pb: 0 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <Box
                  sx={{
                    bgcolor: "primary.main",
                    borderRadius: "12px",
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon sx={{ color: "white" }}>shopping_cart</Icon>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Purchase Request Details
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedRequestDetails.requestId?.toUpperCase() ||
                      selectedRequestDetails._id?.toUpperCase()}
                  </Typography>
                </Box>
                {getStatusChip(selectedRequestDetails.status || "draft")}
                <IconButton
                  onClick={handleDrawerClose}
                  sx={{ ml: 1 }}
                  size="small"
                >
                  <Icon>close</Icon>
                </IconButton>
              </Box>

              {/* Tabs */}
              <Tabs
                value={drawerTab}
                onChange={(_, newValue) => setDrawerTab(newValue)}
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    minHeight: 48,
                  },
                }}
              >
                <Tab
                  label="Details"
                  icon={<Icon>info</Icon>}
                  iconPosition="start"
                />
                <Tab
                  label={`Activity (${activityTotal})`}
                  icon={<Icon>history</Icon>}
                  iconPosition="start"
                />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ flex: 1, overflow: "auto" }}>
              {drawerTab === 0 && (
                <Box sx={{ p: 3 }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                  >
                    <DetailItem
                      icon="confirmation_number"
                      label="Request ID"
                      value={
                        selectedRequestDetails.status === "draft" ||
                        !selectedRequestDetails.requestId ? (
                          <Tooltip
                            title="Submit this purchase request to generate a Request ID."
                            arrow
                          >
                            <Chip
                              label="Not Generated Yet"
                              size="small"
                              sx={{
                                bgcolor: "grey.100",
                                color: "grey.600",
                                fontWeight: 500,
                                fontSize: "0.95em",
                                height: 24,
                                px: 1.5,
                                letterSpacing: 0.2,
                                borderRadius: 1,
                                cursor: "pointer",
                              }}
                            />
                          </Tooltip>
                        ) : (
                          selectedRequestDetails.requestId?.toUpperCase()
                        )
                      }
                    />
                    <DetailItem
                      icon="person"
                      label="Created By"
                      value={
                        selectedRequestDetails.created_by_email ||
                        selectedRequestDetails.created_by ||
                        "Unknown"
                      }
                    />
                    <DetailItem
                      icon="schedule"
                      label="Created Date"
                      value={new Date(
                        selectedRequestDetails.created_at
                      ).toLocaleDateString()}
                    />

                    {selectedRequestDetails.submitted_on && (
                      <>
                        <DetailItem
                          icon="send"
                          label="Submitted Date"
                          value={new Date(
                            selectedRequestDetails.submitted_on
                          ).toLocaleDateString()}
                        />
                        <DetailItem
                          icon="person_outline"
                          label="Submitted By"
                          value={
                            selectedRequestDetails.submitted_by_email ||
                            selectedRequestDetails.submitted_by ||
                            selectedRequestDetails.created_by_email ||
                            selectedRequestDetails.created_by
                          }
                        />
                      </>
                    )}

                    {selectedRequestDetails.approved_on && (
                      <>
                        <DetailItem
                          icon="check_circle"
                          label="Approved Date"
                          value={new Date(
                            selectedRequestDetails.approved_on
                          ).toLocaleDateString()}
                        />
                        <DetailItem
                          icon="verified_user"
                          label="Approved By"
                          value={
                            selectedRequestDetails.approved_by_email ||
                            selectedRequestDetails.approved_by ||
                            "Unknown"
                          }
                        />
                      </>
                    )}

                    {selectedRequestDetails.rejected_on && (
                      <>
                        <DetailItem
                          icon="cancel"
                          label="Rejected Date"
                          value={new Date(
                            selectedRequestDetails.rejected_on
                          ).toLocaleDateString()}
                        />
                        <DetailItem
                          icon="person_off"
                          label="Rejected By"
                          value={
                            selectedRequestDetails.rejected_by_email ||
                            selectedRequestDetails.rejected_by ||
                            "Unknown"
                          }
                        />
                      </>
                    )}

                    <DetailItem
                      icon="fiber_manual_record"
                      label="Status"
                      value={
                        statusConfig[
                          selectedRequestDetails.status as keyof typeof statusConfig
                        ]?.label || "Unknown"
                      }
                      valueColor={
                        statusConfig[
                          selectedRequestDetails.status as keyof typeof statusConfig
                        ]?.color
                      }
                    />

                    {selectedRequestDetails.rejection_reason && (
                      <DetailItem
                        icon="info"
                        label="Rejection Reason"
                        value={selectedRequestDetails.rejection_reason}
                        valueColor="#DC2626"
                      />
                    )}

                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, mb: 2 }}
                      >
                        Requested Items (
                        {selectedRequestDetails.items?.length || 0})
                      </Typography>
                      {selectedRequestDetails.items?.map(
                        (item: any, index: number) => (
                          <Paper
                            key={index}
                            variant="outlined"
                            sx={{ p: 2, mb: 2, borderRadius: 2 }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600 }}
                            >
                              {item.item_name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Quantity: {item.quantity} {item.uom}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Urgency: {item.urgency}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Reason: {item.reason}
                            </Typography>
                          </Paper>
                        )
                      )}
                    </Box>
                  </Box>
                </Box>
              )}

              {drawerTab === 1 && (
                <Box sx={{ p: 3 }}>
                  <ActivityTimeline
                    activities={activityLogs}
                    loading={activityLoading}
                    total={activityTotal}
                    page={activityPage}
                    pageSize={activityPageSize}
                    onPageChange={handleActivityPageChange}
                    showPagination={true}
                  />
                </Box>
              )}
            </Box>

            {/* Action Buttons - Fixed at bottom */}
            {selectedRequestDetails.status === "submitted" && (
              <Box
                sx={{
                  p: 3,
                  pt: 2,
                  bgcolor: "#F8FAFC",
                  borderTop: "1px solid #E2E8F0",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-start",
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={<Icon>check_circle</Icon>}
                    onClick={() => {
                      showActionConfirmation(
                        "approve",
                        () => {
                          handleApproveRequest(selectedRequestDetails._id);
                          handleDrawerClose();
                        },
                        selectedRequestDetails
                      );
                    }}
                    disabled={
                      !hasPermission(
                        "inventory.purchase_requests.approve",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                    }
                    sx={{
                      bgcolor: "#059669",
                      color: "#fff",
                      fontWeight: 700,
                      px: 4,
                      "&:hover": {
                        bgcolor: "#047857",
                        color: "#fff",
                      },
                      boxShadow: 1,
                    }}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Icon>cancel</Icon>}
                    onClick={() => {
                      showRejectionDialog(selectedRequestDetails._id);
                      handleDrawerClose();
                    }}
                    disabled={
                      !hasPermission(
                        "inventory.purchase_requests.reject",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                    }
                    sx={{
                      bgcolor: "#DC2626",
                      color: "#fff",
                      fontWeight: 700,
                      px: 4,
                      "&:hover": {
                        bgcolor: "#B91C1C",
                        color: "#fff",
                      },
                      boxShadow: 1,
                    }}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Icon>refresh</Icon>}
                    onClick={() => {
                      showActionConfirmation(
                        "reopen",
                        () => {
                          handleReopenRequest(selectedRequestDetails);
                          handleDrawerClose();
                        },
                        selectedRequestDetails
                      );
                    }}
                    disabled={
                      !hasPermission(
                        "inventory.purchase_requests.reopen",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                    }
                    sx={{
                      bgcolor: "#F59E0B",
                      color: "#fff",
                      fontWeight: 700,
                      px: 4,
                      "&:hover": {
                        bgcolor: "#B45309",
                        color: "#fff",
                      },
                      boxShadow: 1,
                    }}
                  >
                    Request Changes
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}
      </Drawer>
    </Box>
  );
};

const DetailItem: React.FC<{
  icon: string;
  label: string;
  value: string;
  valueColor?: string;
  isLink?: boolean;
}> = ({ icon, label, value, valueColor, isLink }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "10px",
        bgcolor: "grey.100",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ color: "grey.600", fontSize: 20 }}>{icon}</Icon>
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ display: "block" }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: valueColor || "inherit",
          textDecoration: isLink ? "underline" : "none",
          cursor: isLink ? "pointer" : "default",
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);
